// ============================================
// FILE 6: /frontend/src/components/listings/SaveListingButton.jsx
// PURPOSE: Bookmark/Save listing button component
// ============================================

import { useState, useEffect } from 'react';
import { Bookmark, BookmarkCheck, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { useToast } from '../../hooks/useToast';
import { useAuth } from '../../hooks/useAuth';
import { bookmarksAPI } from '../../api/bookmarks.api';

export default function SaveListingButton({ listingId, size = 'default', className = '', initialBookmarked = null }) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [isBookmarked, setIsBookmarked] = useState(initialBookmarked !== null ? initialBookmarked : false);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(initialBookmarked === null);

  useEffect(() => {
    // Only check individually if no batch data was provided
    if (initialBookmarked === null && user && listingId) {
      checkBookmarkStatus();
    } else {
      setChecking(false);
    }
  }, [user, listingId, initialBookmarked]);

  // Update if parent provides new bookmark status
  useEffect(() => {
    if (initialBookmarked !== null) {
      setIsBookmarked(initialBookmarked);
    }
  }, [initialBookmarked]);

  const checkBookmarkStatus = async () => {
    try {
      const response = await bookmarksAPI.checkBookmark(listingId);
      setIsBookmarked(response.isBookmarked);
    } catch (error) {
      console.error('Failed to check bookmark status:', error);
    } finally {
      setChecking(false);
    }
  };

  const handleToggleBookmark = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!user) {
      toast({
        variant: 'destructive',
        title: 'Login Required',
        description: 'Please login to save listings.',
      });
      return;
    }

    setLoading(true);

    try {
      if (isBookmarked) {
        // Remove bookmark - need to get bookmark ID first
        const response = await bookmarksAPI.checkBookmark(listingId);
        if (response.bookmark) {
          await bookmarksAPI.deleteBookmark(response.bookmark.id);
          setIsBookmarked(false);
          toast({
            title: 'Removed',
            description: 'Listing removed from saved items.',
          });
        }
      } else {
        // Add bookmark
        await bookmarksAPI.createBookmark(listingId);
        setIsBookmarked(true);
        toast({
          title: 'Saved',
          description: 'Listing saved successfully.',
        });
      }
    } catch (error) {
      console.error('Failed to toggle bookmark:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.response?.data?.message || 'Failed to save listing.',
      });
    } finally {
      setLoading(false);
    }
  };

  if (checking) {
    return (
      <Button
        variant="outline"
        size={size}
        disabled
        className={className}
      >
        <Loader2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 animate-spin" />
      </Button>
    );
  }
 
  return (
    <Button
      variant={isBookmarked ? 'default' : 'outline'}
      size={size}
      onClick={handleToggleBookmark}
      disabled={loading}
      className={className}
    >
      {loading ? (
        <Loader2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 animate-spin" />
      ) : isBookmarked ? (
        <>
          <BookmarkCheck className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 sm:mr-2" />
          <span className="text-xs sm:text-sm">Saved</span>
        </>
      ) : (
        <>
          <Bookmark className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 sm:mr-2" />
          <span className="text-xs sm:text-sm">Save</span>
        </>
      )}
    </Button>
  );
}